import { SortableHeader } from '../components/TableControls'
import { sortTableRows, useTableSort } from '../lib/tableControls'
import { displayWorkSummary } from '../../supabase/functions/_shared/workSummary'
import { connectorDateRange } from '../lib/connectorDateRange'
import { buildHookUpdateCommand } from '../lib/agentInstall'
import { activityLabel } from '../../supabase/functions/_shared/activityDetails'
import { ActivitySummaryStatus, type SummaryMetadata } from '../components/ActivitySummaryStatus'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Activity,
  CheckCircle2,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Cpu,
  ClipboardList,
  Coins,
  Pause,
  Pencil,
  Play,
  XCircle,
} from 'lucide-react'

import { supabase, signOut, getCurrentSession } from '../lib/supabase'
import {
  daysAgoKey,
  emptyTotals,
  fetchAgentActivityStatus,
  fetchTeamAgentUsageDaily,
  sumUsage,
  toDateKey,
  type AgentActivityStatus,
  type AgentUsageDailyRow,
} from '../lib/agentUsage'
import { LIST_PRICE_LABEL, TokenBreakdown, UsageCostText } from '../components/UsageCost'
import { AgentStaleBadge, AgentStaleNotice } from '../components/AgentStaleBadge'
import { useAuth, useTeamAccess } from '../contexts/AuthContext'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { Skeleton } from '../components/ui/skeleton'
import { Badge } from '../components/ui/badge'
import { Button, buttonVariants } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

// ============================================================================
// Page: /dashboard/team/:teamId/connectors/:agentId
//
// One agent's detail view, opened by clicking its card on the Agents list
// page (2026-09-06, Figma reference). Decisions made before building this,
// same research-first discipline as the rest of this feature:
//
// - Only 2 of the Figma's 4 stat cards are real: "Tasks Resolved" (count of
//   agent_checkin rows) and "Token Used" (originally agent_checkin.tokens_*;
//   moved to agent_usage_events 2026-10-02, see below). "Uptime" and "Avg. Response" have no data
//   source anywhere in this schema (nothing tracks agent liveness or
//   response latency) and are dropped rather than shown as fabricated
//   numbers. Deltas are phrased as "N more/fewer than last week" (absolute,
//   rolling 7-day windows) rather than the mock's percentages, matching the
//   delta style already used on Check-ins/Summary — this app doesn't use
//   percentage deltas anywhere else.
// - "Pause agent" is real (2026-10-03): toggles agents.paused_at (see
//   20261003090000_add_agent_pause_feature.sql). Status reflects paused_at
//   (Paused = secondary badge, Active = success badge). agent-auto-checkin
//   and agent-checkins both reject new check-ins from a paused agent with a
//   403 (same migration's follow-up commit) -- pausing is a real stop, not
//   just a display state.
// - "Edit" reopens the same rename-only Dialog already built on the Agents
//   list page. "Remove agent" reuses the same manage-agents soft-delete.
//   Edit/Pause/Resume were owner-only at first (direct client RLS update,
//   no edge function) until an explicit follow-up request (2026-10-03) to
//   let an admin, or a manager of the owner's pod, use them on an agent
//   they don't own too -- same rule Remove already had. All three now route
//   through manage-agents (see that function's own header for the
//   authorization change), gated here by isOwner || canManageAgent.
// - Model reads "Claude Code" (static) — same placeholder as the list page,
//   not the mock's per-agent "GPT-4o" value.
// - "Connected tools" (Slack/Figma in the mock) was removed 2026-09-06
//   (explicit request) — no real tool-integration concept in phase 1, so
//   this card doesn't render one at all.
// - "View full history" expands the already-fetched check-in list in place
//   rather than linking to a separate paginated route — there's no reason
//   to round-trip the server again for what's already in memory.
// ============================================================================

interface AgentDetailProps {
  teamId: string
  agentId: string
}

interface AgentRow {
  id: string
  name: string
  user_id: string
  created_at: string
  project_id: string | null
  paused_at: string | null
}

interface ProjectOption {
  id: string
  name: string
}

interface Member {
  id: string
  name: string | null
  email: string | null
  avatar_url: string | null
  pod_id: string | null
}

interface CheckinRow extends SummaryMetadata {
  id: string
  task: string | null
  notes: string | null
  created_at: string
  model: string | null
  files_changed: number | null
  git_branch: string | null
  tokens_input: number | null
  tokens_output: number | null
  activity_kind: 'activity' | 'completed_work'
  classification?: string | null
}

const AVATAR_PALETTE = [
  'bg-sky-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-fuchsia-500',
  'bg-cyan-500',
  'bg-violet-500',
  'bg-emerald-500',
]

function paletteFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
}

function initialsOf(name: string): string {
  const initials = name
    .split(/[\s-]+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return initials || '?'
}

function displayName(member: Pick<Member, 'name' | 'email'>): string {
  return member.name || member.email?.split('@')[0] || 'Unknown user'
}

function formatDelta(diff: number, unit: string): { text: string; icon: typeof CheckCircle2 } {
  if (diff === 0) return { text: `Same as last week`, icon: CheckCircle2 }
  const word = diff > 0 ? 'more' : 'fewer'
  return { text: `${Math.abs(diff)}${unit} ${word} than last week`, icon: CheckCircle2 }
}

function formatCheckinTime(iso: string): string {
  const date = new Date(iso)
  const today = new Date()
  const isToday = date.toDateString() === today.toDateString()
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  if (isToday) return `Today, ${time}`
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`
}

type ActivityRange = '7' | '10' | '30' | 'all'

const AgentDetail: React.FC<AgentDetailProps> = ({ teamId, agentId }) => {
  const navigate = useNavigate()
  const { role } = useTeamAccess(teamId)
  const { userTeams, user } = useAuth()
  const isAdmin = role === 'admin' || role === 'owner'
  const isManager = role === 'manager'
  const myUserId = userTeams?.find((tm) => tm.team_id === teamId)?.user_id
  // Managers only: pods they manage, so "Remove agent" can show for an
  // agent whose owner is in one of those pods -- same logic and same
  // manage-agents server-side check as TeamAgents.tsx's card grid.
  const [managedPodIds, setManagedPodIds] = useState<Set<string>>(new Set())

  const [teamName, setTeamName] = useState<string | undefined>(undefined)
  const [teamTimezone, setTeamTimezone] = useState('UTC')
  const [agent, setAgent] = useState<AgentRow | null>(null)
  const [owner, setOwner] = useState<Member | null>(null)
  const [teamLabel, setTeamLabel] = useState('—')
  const [checkins, setCheckins] = useState<CheckinRow[]>([])
  const [checkinsLoading, setCheckinsLoading] = useState(false)
  const [activityRange, setActivityRange] = useState<ActivityRange>('7')
  const [modelFilter, setModelFilter] = useState('all')
  const { sort, toggleSort } = useTableSort('checkins', 'desc')
  const [expandedCheckinId, setExpandedCheckinId] = useState<string | null>(null)
  const [usageRows, setUsageRows] = useState<AgentUsageDailyRow[]>([])
  const [activity, setActivity] = useState<AgentActivityStatus | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [renameOpen, setRenameOpen] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [renaming, setRenaming] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const [projects, setProjects] = useState<ProjectOption[]>([])
  const [savingProject, setSavingProject] = useState(false)
  const [pausing, setPausing] = useState(false)
  const [teamPodIds, setTeamPodIds] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      const [teamResult, agentResult] = await Promise.all([
        supabase.from('teams').select('name, standup_config').eq('id', teamId).single(),
        (supabase.from as any)('agents_team_visible')
          .select('id, name, user_id, created_at, project_id, paused_at')
          .eq('team_id', teamId)
          .eq('id', agentId)
          .maybeSingle(),
      ])

      if (cancelled) return

      if (teamResult.error || agentResult.error || !agentResult.data) {
        console.error('Failed to load agent:', teamResult.error ?? agentResult.error)
        setError('Could not find this connector.')
        setLoading(false)
        return
      }

      setTeamName(teamResult.data?.name ?? undefined)
      setTeamTimezone((teamResult.data?.standup_config as { timezone?: string } | null)?.timezone || 'UTC')
      const agentRow = agentResult.data as AgentRow
      setAgent(agentRow)

      const [ownerResult, podsResult, projectsResult, usageResult] = await Promise.all([
        (supabase.from as any)('users')
          .select('id, name, email, avatar_url, pod_id')
          .eq('id', agentRow.user_id)
          .maybeSingle(),
        (supabase.from as any)('pods').select('id, name').eq('team_id', teamId),
        (supabase.from as any)('projects')
          .select('id, name')
          .eq('team_id', teamId)
          .is('deleted_at', null)
          .order('name', { ascending: true }),
        // All-time usage for this one agent (install date -> today). If the
        // caller can't see this agent in detail the RPC returns its usage
        // with agent_id NULL, so the filter matches nothing and the card
        // shows zeros -- the same as agent_checkin's RLS gives for tasks.
        // The client-side filter is a belt-and-braces guard against ever
        // counting a null-agent (other agents') row here.
        fetchTeamAgentUsageDaily(teamId, agentRow.created_at.slice(0, 10), toDateKey(new Date()), {
          agentIds: [agentId],
        }).then((rows) => rows.filter((r) => r.agentId === agentId)).catch((err: unknown) => {
          console.error('Failed to load agent usage:', err)
          return [] as AgentUsageDailyRow[]
        }),
      ])

      if (cancelled) return

      const ownerRow = (ownerResult.data as Member | null) ?? null
      setOwner(ownerRow)
      const pods = (podsResult.data ?? []) as { id: string; name: string }[]
      const podsById = new Map(pods.map((p) => [p.id, p.name]))
      setTeamLabel(ownerRow?.pod_id ? podsById.get(ownerRow.pod_id) ?? '—' : '—')
      setTeamPodIds(pods.map((p) => p.id))
      setUsageRows(usageResult)
      setProjects((projectsResult.data ?? []) as ProjectOption[])
      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [teamId, agentId])

  useEffect(() => {
    if (!agent) return
    let cancelled = false
    setCheckinsLoading(true)

    const range = connectorDateRange(activityRange === 'all' ? null : Number(activityRange), teamTimezone)

    ;(supabase.rpc as any)('get_agent_activity', {
      p_team_id: teamId,
      p_agent_id: agent.id,
      p_from: range.from,
      p_to: range.to,
      p_page_size: 100,
      p_page: 0,
    }).then(({ data, error }: { data: CheckinRow[] | null; error: unknown }) => {
      if (cancelled) return
      if (error) {
        console.error('Failed to load agent activity:', error)
        setCheckins([])
      } else {
        setCheckins(data ?? [])
      }
      setCheckinsLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [teamId, agent?.id, activityRange, teamTimezone])

  // Separate from the load effect above for the same reason as
  // TeamAgents.tsx's identical effect: `role`/`user` resolve asynchronously
  // via useTeamAccess/useAuth and can still be settling when the load effect
  // fires, so this re-runs once they (and teamPodIds) are actually ready.
  useEffect(() => {
    let cancelled = false

    const loadManagedPods = async () => {
      if (!isManager || !user?.id || teamPodIds.length === 0) {
        setManagedPodIds(new Set())
        return
      }
      const { data, error } = await (supabase.from as any)('pod_managers')
        .select('pod_id')
        .eq('auth_user_id', user.id)
        .in('pod_id', teamPodIds)
      if (cancelled) return
      if (error) {
        console.error('Failed to load managed pods:', error)
        return
      }
      setManagedPodIds(new Set((data ?? []).map((r: { pod_id: string }) => r.pod_id)))
    }

    loadManagedPods()
    return () => {
      cancelled = true
    }
  }, [isManager, user?.id, teamPodIds])

  // Dead-hook warning. Admins/managers get an exact last-activity timestamp
  // from the raw tables; the usage RPC only attributes usage to agents the
  // caller may see in detail, so for anyone but an admin "no data" means
  // "unknown", not "stale" (see fetchAgentActivityStatus).
  const canReadRaw = isAdmin || isManager
  useEffect(() => {
    if (!agent) return
    let cancelled = false
    fetchAgentActivityStatus(teamId, [{ id: agent.id, created_at: agent.created_at }], {
      canReadRaw,
      canSeeAllAgents: isAdmin,
    })
      .then((map) => {
        if (!cancelled) setActivity(map.get(agent.id))
      })
      .catch((err) => console.error('Failed to load agent activity status:', err))
    return () => {
      cancelled = true
    }
  }, [teamId, agent?.id, agent?.created_at, canReadRaw, isAdmin])

  const goToAgents = () => navigate(`/dashboard/team/${teamId}/connectors`)
  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const isOwner = !!myUserId && agent?.user_id === myUserId
  // Admins can act on any agent; managers only one whose owner is in a pod
  // they manage -- mirrors TeamAgents.tsx's canDelete and the real check in
  // manage-agents/index.ts, which is the actual enforcement boundary.
  // 2026-10-03: renamed from canDelete (gated Remove only) to canManageAgent
  // now that the same rule also gates Edit and Pause/Resume for a non-owner
  // admin/manager -- TeamAgents.tsx's own canDelete is untouched, it still
  // only gates that grid's trash icon, not asked to change here.
  const canManageAgent = isAdmin || (isManager && !!owner?.pod_id && managedPodIds.has(owner.pod_id))
  const ownerName = owner ? displayName(owner) : 'Unknown owner'
  const projectName = agent?.project_id
    ? projects.find((p) => p.id === agent.project_id)?.name ?? 'Unknown project'
    : null

  const availableModels = useMemo(
    () => Array.from(new Set(checkins.map((checkin) => checkin.model).filter(Boolean) as string[])).sort(),
    [checkins]
  )
  const filteredCheckins = useMemo(
    () => modelFilter === 'all' ? checkins : checkins.filter((checkin) => (checkin.model || 'Model not reported') === modelFilter),
    [checkins, modelFilter]
  )

  const stats = useMemo(() => {
    // Token weeks are keyed by usage_date: [today-6, today] vs [today-13, today-7].
    const thisWeekFrom = daysAgoKey(6)
    const lastWeekFrom = daysAgoKey(13)
    const thisWeek = sumUsage(usageRows.filter((r) => r.usageDate >= thisWeekFrom))
    const lastWeek = sumUsage(
      usageRows.filter((r) => r.usageDate >= lastWeekFrom && r.usageDate < thisWeekFrom)
    )
    const usage = usageRows.length > 0 ? sumUsage(usageRows) : emptyTotals()

    return {
      totalUpdates: filteredCheckins.length,
      usage,
      tokenDelta: formatDelta(thisWeek.totalTokens - lastWeek.totalTokens, ' tokens'),
    }
  }, [filteredCheckins, usageRows])

  const modelUsage = useMemo(() => {
    const byModel = new Map<string, { checkins: number; tokens: number; files: number }>()
    for (const checkin of filteredCheckins) {
      const model = checkin.model || 'Model not reported'
      const current = byModel.get(model) ?? { checkins: 0, tokens: 0, files: 0 }
      current.checkins += 1
      current.tokens += (checkin.tokens_input ?? 0) + (checkin.tokens_output ?? 0)
      current.files += checkin.files_changed ?? 0
      byModel.set(model, current)
    }
    return sortTableRows(Array.from(byModel.entries()), sort, {
      model: row => row[0], checkins: row => row[1].checkins, tokens: row => row[1].tokens, files: row => row[1].files,
    })
  }, [filteredCheckins, sort])

  const openRename = () => {
    if (!agent) return
    setRenameValue(agent.name)
    setRenameOpen(true)
  }

  const handleRenameSubmit = async () => {
    if (!agent) return
    const trimmed = renameValue.trim().slice(0, 60)
    if (!trimmed || trimmed === agent.name) {
      setRenameOpen(false)
      return
    }

    setRenaming(true)
    try {
      // Routed through manage-agents (2026-10-03), not a direct client
      // update -- this control is now reachable by an admin/manager acting
      // on someone else's agent (see isOwner || canManageAgent gate below), and
      // agents_update RLS only allows the owner's own row, so a non-owner's
      // direct update would silently no-op rather than error. The edge
      // function's own authorization (owner OR admin OR manager-of-pod) is
      // the real boundary; this page's gate just decides what renders.
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { error: renameError } = await supabase.functions.invoke('manage-agents', {
        body: { action: 'rename', team_id: teamId, agent_id: agent.id, name: trimmed },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })

      if (renameError) throw renameError
      toast.success('Connector renamed')
      setAgent((prev) => (prev ? { ...prev, name: trimmed } : prev))
      setRenameOpen(false)
    } catch (err) {
      console.error('Failed to rename agent:', err)
      toast.error('Could not rename this connector')
    } finally {
      setRenaming(false)
    }
  }

  const handleProjectChange = async (nextProjectId: string) => {
    if (!agent || !myUserId) return
    const value = nextProjectId === 'unassigned' ? null : nextProjectId
    if (value === agent.project_id) return

    setSavingProject(true)
    try {
      // Self-service, direct client update -- same pattern as
      // handleRenameSubmit above: relies on the existing agents_update RLS
      // policy (owner + team-member), not a separate edge function. Only
      // the agent's own owner can reach this control (see isOwner gate
      // below), matching that RLS requirement.
      const { error: updateError } = await (supabase.from as any)('agents')
        .update({ project_id: value })
        .eq('id', agent.id)
        .eq('user_id', myUserId)

      if (updateError) throw updateError
      setAgent((prev) => (prev ? { ...prev, project_id: value } : prev))
      toast.success(value ? 'Project updated' : 'Project unassigned')
    } catch (err) {
      console.error('Failed to update agent project:', err)
      toast.error('Could not update this connector\'s project')
    } finally {
      setSavingProject(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!agent) return
    setDeleting(true)
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { error: fnError } = await supabase.functions.invoke('manage-agents', {
        body: { action: 'delete', team_id: teamId, agent_id: agent.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (fnError) throw fnError
      toast.success('Connector removed')
      goToAgents()
    } catch (err) {
      console.error('Failed to delete agent:', err)
      toast.error('Could not remove this connector')
      setDeleting(false)
    }
  }

  const handlePause = async () => {
    if (!agent) return
    const pausingNow = !agent.paused_at

    setPausing(true)
    try {
      // Routed through manage-agents (2026-10-03) -- see handleRenameSubmit's
      // comment just above for why this moved off a direct client update:
      // an admin/manager can now reach this control for an agent they don't
      // own (isOwner || canManageAgent gate below), and the owner-only
      // agents_update RLS policy would silently no-op that update instead of
      // erroring. The edge function checks owner OR admin OR
      // manager-of-pod; this page's gate only decides what renders.
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { error: updateError } = await supabase.functions.invoke('manage-agents', {
        body: { action: pausingNow ? 'pause' : 'resume', team_id: teamId, agent_id: agent.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })

      if (updateError) throw updateError
      const nextValue = pausingNow ? new Date().toISOString() : null
      setAgent((prev) => (prev ? { ...prev, paused_at: nextValue } : prev))
      toast.success(pausingNow ? 'Connector paused' : 'Connector resumed')
    } catch (err) {
      console.error('Failed to update agent pause state:', err)
      toast.error(pausingNow ? 'Could not pause this connector' : 'Could not resume this connector')
    } finally {
      setPausing(false)
    }
  }

  return (
    <SidebarProvider>
      <AppSidebar
        activeSection="agents"
        onNavigate={goToAgents}
        onOpenTeamManagement={() => navigate(`/dashboard/team/${teamId}/team-management`)}
        onOpenBilling={() => navigate(`/dashboard/team/${teamId}?open=billing`)}
        teamId={teamId}
      />
      <SidebarInset>
        <TopBar teamId={teamId} teamName={teamName} onLogout={handleLogout} sectionLabel="Connectors" />
        <main className="space-y-6 bg-background p-4 sm:p-6">
          <Button
            type="button"
            onClick={goToAgents}
            variant="ghost"
            className="h-auto gap-2 p-0 text-sm font-medium text-primary hover:bg-transparent hover:text-primary/80"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to connectors
          </Button>

          {loading && (
            // Shape mirrors the loaded page: avatar + name/subtitle header,
            // the two stat cards, and the check-in history / configuration
            // card pair below -- so the shimmer reads as "agent detail
            // incoming" rather than one blob.
            //
            // Every card on this page is a plain div + bg-background, not the
            // shadcn <Card> (bg-card) (2026-10-03) -- bg-card is pure white
            // and reads as a glaringly white box against the cream
            // --background page tone (see src/index.css); every other
            // restyled page (TeamManagement.tsx, Summary, Check-ins, the
            // Agents grid) uses this same convention, see UI_STYLE_GUIDE.md.
            <>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <Skeleton className="h-12 w-12 shrink-0 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-40" />
                    <Skeleton className="h-4 w-56" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[0, 1].map((i) => (
                  <div key={i} className="border border-border bg-background shadow-sm">
                    <div className="space-y-2 p-4 sm:p-5">
                      <Skeleton className="h-3 w-28" />
                      <Skeleton className="h-7 w-20" />
                      <Skeleton className="h-3 w-36" />
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="border border-border bg-background shadow-sm">
                  <div className="space-y-4 p-4 sm:p-6">
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-36" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                    <div className="space-y-3">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="flex items-center justify-between gap-3">
                          <Skeleton className="h-4 w-44" />
                          <Skeleton className="h-3 w-16 shrink-0" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="border border-border bg-background shadow-sm">
                  <div className="space-y-4 p-4 sm:p-6">
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-3 w-40" />
                    </div>
                    <div className="divide-y divide-border">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex items-center justify-between py-2">
                          <Skeleton className="h-3 w-20" />
                          <Skeleton className="h-4 w-24" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}

          {!loading && !error && agent && (
            <>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarFallback
                      className={`text-base font-semibold text-white ${paletteFor(agent.id)}`}
                    >
                      {initialsOf(agent.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <h1 className="truncate text-2xl font-semibold text-foreground">{agent.name}</h1>
                    <p className="text-sm text-muted-foreground">
                      Claude Code · owned by {ownerName} · in {teamLabel}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {(isOwner || canManageAgent) && (
                    <Button variant="outline" onClick={openRename} className="gap-1.5">
                      <Pencil className="h-4 w-4" />
                      Edit
                    </Button>
                  )}
                  {canManageAgent && (
                    <Button
                      variant="outline"
                      onClick={() => setDeleteConfirmOpen(true)}
                      disabled={deleting}
                      className="gap-1.5"
                    >
                      <XCircle className="h-4 w-4" />
                        {deleting ? 'Removing…' : 'Remove connector'}
                    </Button>
                  )}
                  {(isOwner || canManageAgent) && (
                    <Button
                      variant={agent.paused_at ? 'outline' : 'default'}
                      onClick={handlePause}
                      disabled={pausing}
                      className="gap-1.5"
                    >
                      {agent.paused_at ? (
                        <Play className="h-4 w-4" />
                      ) : (
                        <Pause className="h-4 w-4" />
                      )}
                      {pausing ? 'Saving…' : agent.paused_at ? 'Resume connector' : 'Pause connector'}
                    </Button>
                  )}
                </div>
              </div>

              <AgentStaleNotice status={activity} />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="border border-border bg-background shadow-sm">
                  <div className="space-y-2 p-4 sm:p-5">
                    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <ClipboardList className="h-4 w-4" />
                      <span>Recorded activity</span>
                    </div>
                    <div className="text-2xl font-semibold text-foreground sm:text-3xl">
                      {stats.totalUpdates}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {activityRange === 'all'
                        ? 'All recorded activity'
                        : `In the last ${activityRange} days`}
                    </p>
                  </div>
                </div>
                <div className="border border-border bg-background shadow-sm">
                  <div className="space-y-2 p-4 sm:p-5">
                    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <Coins className="h-4 w-4" />
                      <span>Tokens Used</span>
                    </div>
                    <div className="text-2xl font-semibold text-foreground sm:text-3xl">
                      {stats.usage.totalTokens.toLocaleString()}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {stats.tokenDelta.text}
                      {stats.usage.eventCount > 0 && (
                        <>
                          {' · '}
                          <UsageCostText totals={stats.usage} /> {LIST_PRICE_LABEL.toLowerCase()}
                        </>
                      )}
                    </p>
                    <TokenBreakdown totals={stats.usage} className="border-t border-border pt-2" />
                  </div>
                </div>
              </div>

              {modelUsage.length > 0 && (
                <div className="border border-border bg-background shadow-sm">
                  <div className="space-y-4 p-4 sm:p-5">
                    <div className="flex items-start gap-2">
                      <Cpu className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Model usage</h2>
                        <p className="text-sm text-muted-foreground">
                          Model and check-in metadata for the selected activity range.
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[520px] text-sm">
                        <thead>
                          <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            <SortableHeader className="pb-2 pr-4" column="model" label="Model" sort={sort} onSort={toggleSort} />
                            <SortableHeader className="pb-2 pr-4" column="checkins" label="Check-ins" sort={sort} onSort={toggleSort} />
                            <SortableHeader className="pb-2 pr-4" column="tokens" label="Tokens" sort={sort} onSort={toggleSort} />
                            <SortableHeader className="pb-2" column="files" label="Files changed" sort={sort} onSort={toggleSort} />
                          </tr>
                        </thead>
                        <tbody>
                          {modelUsage.map(([model, summary]) => (
                            <tr key={model} className="border-b border-border last:border-0">
                              <td className="py-3 pr-4 font-medium text-foreground">{model}</td>
                              <td className="py-3 pr-4 text-muted-foreground">{summary.checkins}</td>
                              <td className="py-3 pr-4 text-muted-foreground">{summary.tokens.toLocaleString()}</td>
                              <td className="py-3 text-muted-foreground">{summary.files}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="border border-border bg-background shadow-sm">
                  <div className="space-y-4 p-4 sm:p-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Activity history</h2>
                        <p className="text-sm text-muted-foreground">
                          Conversations, investigations, and file updates reported by this connector.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Select value={modelFilter} onValueChange={setModelFilter}>
                          <SelectTrigger className="h-8 w-[150px] text-sm" aria-label="Filter by model">
                            <SelectValue placeholder="All models" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All models</SelectItem>
                            {availableModels.map((model) => (
                              <SelectItem key={model} value={model}>{model}</SelectItem>
                            ))}
                            {checkins.some((checkin) => !checkin.model) && (
                              <SelectItem value="Model not reported">Model not reported</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <CalendarDays className="h-4 w-4 text-muted-foreground" />
                        <Select
                          value={activityRange}
                          onValueChange={(value) => setActivityRange(value as ActivityRange)}
                        >
                          <SelectTrigger className="h-8 w-[132px] text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">Last 7 days</SelectItem>
                            <SelectItem value="10">Last 10 days</SelectItem>
                            <SelectItem value="30">Last 30 days</SelectItem>
                            <SelectItem value="all">All activity</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {checkinsLoading ? (
                      <p className="text-sm text-muted-foreground">Loading activity…</p>
                    ) : filteredCheckins.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {checkins.length === 0 ? 'No check-ins in this range.' : 'No check-ins match this model.'}
                      </p>
                    ) : (
                      <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
                        {filteredCheckins.map((c, i) => {
                          const checkinKey = c.id || `${c.created_at}-${i}`
                          const expanded = expandedCheckinId === checkinKey
                          return (
                          <div key={checkinKey} className="border border-border">
                            <button
                              type="button"
                              aria-expanded={expanded}
                              onClick={() => setExpandedCheckinId(expanded ? null : checkinKey)}
                              className="flex w-full items-start justify-between gap-3 p-3 text-left hover:bg-muted/30"
                            >
                              <div className="flex min-w-0 items-start gap-2">
                                {c.classification === 'file_change' ? (
                                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                                ) : c.classification === 'failed_attempt' ? (
                                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                                ) : (
                                  <Activity className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                )}
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium text-foreground">
                                    {c.task || 'No task recorded'}
                                  </p>
                                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                    <span>{activityLabel(c.classification, c.activity_kind)} · {c.model || 'Model not reported'} · {c.files_changed ?? 0} files changed</span>
                                    <ActivitySummaryStatus activity={c} />
                                  </div>
                                </div>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <span className="text-xs text-muted-foreground">{formatCheckinTime(c.created_at)}</span>
                                {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                              </div>
                            </button>
                            {expanded && (
                              <div className="space-y-2 border-t border-border bg-muted/20 px-3 py-3 text-xs text-muted-foreground">
                                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
                                  {c.task || 'No task recorded'}
                                </p>
                                <ActivitySummaryStatus activity={c} detailed />
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                  <span>Model: <strong className="font-medium text-foreground">{c.model || 'Not reported'}</strong></span>
                                  <span>Branch: <strong className="font-medium text-foreground">{c.git_branch || 'Not reported'}</strong></span>
                                  <span>Input tokens: <strong className="font-medium text-foreground">{(c.tokens_input ?? 0).toLocaleString()}</strong></span>
                                  <span>Output tokens: <strong className="font-medium text-foreground">{(c.tokens_output ?? 0).toLocaleString()}</strong></span>
                                </div>
                                {c.notes && <p className="border-t border-border pt-2">{c.notes}</p>}
                              </div>
                            )}
                          </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border border-border bg-background shadow-sm">
                  <div className="space-y-4 p-4 sm:p-6">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Configuration</h2>
                      <p className="text-sm text-muted-foreground">
                        Static details about this connector.
                      </p>
                    </div>
                    <div className="divide-y divide-border text-sm">
                      <div className="flex items-center justify-between py-2">
                        <span className="text-muted-foreground">Model</span>
                        <span className="font-medium text-foreground">Claude Code</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-muted-foreground">Team</span>
                        <span className="font-medium text-foreground">{teamLabel}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3 py-2">
                        <span className="shrink-0 text-muted-foreground">Project</span>
                        {isOwner ? (
                          <Select
                            value={agent.project_id ?? 'unassigned'}
                            onValueChange={handleProjectChange}
                            disabled={savingProject}
                          >
                            <SelectTrigger className="h-8 w-[180px] text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {projects.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="font-medium text-foreground">
                            {projectName ?? 'Unassigned'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-muted-foreground">Owner</span>
                        <div className="flex items-center gap-1.5 font-medium text-foreground">
                          <Avatar className="h-5 w-5">
                            {owner?.avatar_url && <AvatarImage src={owner.avatar_url} alt="" />}
                            <AvatarFallback className="text-[10px]">
                              {initialsOf(ownerName)}
                            </AvatarFallback>
                          </Avatar>
                          <span>{ownerName}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 py-2">
                        <span className="text-muted-foreground">Status</span>
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          <AgentStaleBadge status={activity} />
                          {agent.paused_at ? (
                            <Badge variant="secondary" className="font-medium">
                              Paused
                            </Badge>
                          ) : (
                            <Badge variant="success" className="font-medium">
                              Active
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-muted-foreground">Created</span>
                        <span className="font-medium text-foreground">
                          {new Date(agent.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                    {isOwner && (
                      <div className="space-y-3 border-t border-border pt-4">
                        <h3 className="text-sm font-medium text-foreground">Automatic connector updates</h3>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          Run this once on the computer where this connector is installed. It enables automatic updates while preserving your token and history.
                        </p>
                        <code className="block break-all text-xs text-muted-foreground">{buildHookUpdateCommand()}</code>
                        <Button variant="outline" size="sm" onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(buildHookUpdateCommand())
                            toast.success('Update command copied')
                          } catch { toast.error('Could not copy the update command') }
                        }}>Copy update command</Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </SidebarInset>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename connector</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <label className="text-sm font-medium text-foreground">Connector name</label>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit()
              }}
              maxLength={60}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenameSubmit} disabled={renaming || !renameValue.trim()}>
              {renaming ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{agent?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This stops its check-in reminders.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: 'destructive' })}
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault()
                handleConfirmDelete()
              }}
            >
              {deleting ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  )
}

export default AgentDetail
